import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  addActionFromFinding,
  addManualAction,
  getSnapshot,
  removeAction,
  subscribe,
  type BuildActionOptions,
  type ManualActionInput,
  type UserAction,
} from "@/utils/userActionsStore";
import type { TaskTableRow } from "@/views/Risk/Tasks/utils";

/**
 * The user's own actions, live. Every consumer re-renders when one is added or
 * removed, which is what lets the nav count pill tick up while the Trial News
 * drawer is open over the top of it.
 */
export function useUserActions() {
  const actions = useSyncExternalStore<UserAction[]>(subscribe, getSnapshot);

  const actionRows = useMemo<TaskTableRow[]>(
    () => actions.map((action) => action.row),
    [actions],
  );

  // Which findings already have an action, so a finding card can show that it
  // has been added instead of offering to add it again.
  const addedFindingIds = useMemo(
    () =>
      new Set(
        actions
          .map((action) => action.findingId)
          .filter((id): id is string => !!id),
      ),
    [actions],
  );

  const addFromFinding = useCallback(
    (options: BuildActionOptions) => addActionFromFinding(options),
    [],
  );

  const addManual = useCallback(
    (input: ManualActionInput) => addManualAction(input),
    [],
  );

  const remove = useCallback((taskId: string) => removeAction(taskId), []);

  return {
    actions,
    actionRows,
    addedFindingIds,
    count: actions.length,
    addFromFinding,
    addManual,
    remove,
  };
}
